import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Spinner } from "../../components/Brand";
import LineItemsEditor from "../admin/LineItemsEditor";

/**
 * Admin-only "Billing" panel shown on the customer BookingDetail page.
 * Lists all quotations + tax invoices linked to this booking, and lets the
 * admin generate a new quotation from the booking with one click (opens a
 * pre-filled editor).
 */
const QUOTE_COLORS = {
    draft: "bg-white/10 text-white/70 border-white/20",
    sent: "bg-blue-500/20 text-blue-300 border-blue-500/40",
    viewed: "bg-cyan-500/20 text-cyan-300 border-cyan-500/40",
    accepted: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
    rejected: "bg-rose-500/20 text-rose-300 border-rose-500/40",
    expired: "bg-amber-500/20 text-amber-300 border-amber-500/40",
    converted: "bg-[#D4AF37]/20 text-[#B2912A] border-[#D4AF37]/40",
};
const INV_COLORS = {
    draft: "bg-slate-200 text-slate-700 border-slate-300",
    issued: "bg-blue-100 text-blue-700 border-blue-300",
    partially_paid: "bg-amber-100 text-amber-800 border-amber-300",
    paid: "bg-emerald-100 text-emerald-700 border-emerald-300",
    cancelled: "bg-slate-200 text-slate-500 border-slate-300",
    refunded: "bg-purple-100 text-purple-700 border-purple-300",
    void: "bg-rose-100 text-rose-700 border-rose-300",
};

function money(v, cur = "AED") { return `${cur} ${(+v || 0).toFixed(2)}`; }

export default function BookingBillingPanel({ bookingId }) {
    const [links, setLinks] = useState({ quotations: [], invoices: [] });
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/bookings/${bookingId}/billing-links`);
            setLinks(data);
        } catch { /* silent — admin-only view */ }
        finally { setLoading(false); }
    }, [bookingId]);

    useEffect(() => { load(); }, [load]);

    const openGenerate = async () => {
        try {
            const { data } = await api.get(`/bookings/${bookingId}/billing-prefill`);
            setForm({
                booking_id: data.booking_id,
                booking_number: data.booking_number,
                booking_service_name: data.booking_service_name,
                booking_technician_name: data.booking_technician_name,
                customer_name: data.customer_name || "",
                customer_email: data.customer_email || "",
                customer_phone: data.customer_phone || "",
                customer_company: data.customer_company || "",
                customer_address: data.customer_address || "",
                issue_date: "",
                valid_until: "",
                currency: data.currency || "AED",
                vat_rate: 5,
                items: data.items || [],
                notes: "",
                terms: "",
            });
            setShowForm(true);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Failed to open form");
        }
    };

    const save = async (e) => {
        e.preventDefault();
        if (!form.customer_name.trim()) { toast.error("Customer name is required"); return; }
        if (form.items.length === 0) { toast.error("Add at least one line item"); return; }
        setSaving(true);
        try {
            const payload = {
                booking_id: form.booking_id,
                customer_name: form.customer_name,
                customer_email: form.customer_email || "",
                customer_phone: form.customer_phone,
                customer_company: form.customer_company,
                customer_address: form.customer_address,
                currency: form.currency,
                vat_rate: +form.vat_rate,
                items: form.items.map((it) => ({
                    part_id: it.part_id || null,
                    description: (it.description || "").trim(),
                    unit: it.unit || "pc",
                    qty: +it.qty,
                    unit_price: +it.unit_price,
                    discount_pct: +it.discount_pct || 0,
                    note: it.note || "",
                })),
                notes: form.notes,
                terms: form.terms,
            };
            if (form.issue_date) payload.issue_date = form.issue_date;
            if (form.valid_until) payload.valid_until = form.valid_until;
            const { data } = await api.post("/quotations", payload);
            toast.success(`${data.number} created`);
            setShowForm(false);
            load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return null;
    const hasAny = links.quotations.length + links.invoices.length > 0;

    return (
        <div className="lx-card p-7 mb-6" data-testid="booking-billing-panel">
            <div className="flex items-center justify-between mb-5">
                <div>
                    <div className="lx-label text-[#B2912A]">Billing</div>
                    <div className="text-xs text-[#555] mt-1">Quotations & tax invoices for this booking</div>
                </div>
                <button
                    type="button"
                    data-testid="booking-generate-quote-btn"
                    onClick={openGenerate}
                    className="lx-btn-gold px-5 py-2.5 text-xs uppercase tracking-wider"
                >
                    + Generate Quotation
                </button>
            </div>

            {!hasAny && (
                <div className="text-sm text-[#555] py-3">No quotations or invoices yet. Generate one to get started.</div>
            )}

            {links.quotations.length > 0 && (
                <div className="mb-4">
                    <div className="text-[10px] uppercase tracking-widest text-[#B2912A] mb-2">Quotations</div>
                    <div className="space-y-1.5">
                        {links.quotations.map((q) => (
                            <Link
                                key={q.id}
                                to="/admin/quotations"
                                data-testid={`bkg-quote-${q.id}`}
                                className="flex items-center justify-between bg-[#FAFAF8] hover:bg-[#F3F2F0] transition px-4 py-2.5 border border-[#E5E2DC]"
                            >
                                <div className="flex items-center gap-3">
                                    <span className="font-mono text-sm text-[#180A21]">{q.number}</span>
                                    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider border ${QUOTE_COLORS[q.status] || QUOTE_COLORS.draft}`}>{q.status}</span>
                                </div>
                                <div className="text-sm text-[#180A21] tabular-nums">{money(q.total_amount, q.currency)}</div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {links.invoices.length > 0 && (
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#B2912A] mb-2">Tax Invoices</div>
                    <div className="space-y-1.5">
                        {links.invoices.map((inv) => (
                            <Link
                                key={inv.id}
                                to="/admin/tax-invoices"
                                data-testid={`bkg-invoice-${inv.id}`}
                                className="flex items-center justify-between bg-[#FAFAF8] hover:bg-[#F3F2F0] transition px-4 py-2.5 border border-[#E5E2DC]"
                            >
                                <div className="flex items-center gap-3">
                                    <span className="font-mono text-sm text-[#180A21]">{inv.number}</span>
                                    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider border ${INV_COLORS[inv.status] || INV_COLORS.draft}`}>{inv.status.replace("_", " ")}</span>
                                    {inv.linked_quotation_number && (
                                        <span className="text-[10px] text-[#555]">from {inv.linked_quotation_number}</span>
                                    )}
                                </div>
                                <div className="text-sm text-[#180A21] tabular-nums">
                                    {inv.status === "partially_paid" ? (
                                        <span>{money(inv.amount_paid, inv.currency)} / {money(inv.total_amount, inv.currency)}</span>
                                    ) : (
                                        money(inv.total_amount, inv.currency)
                                    )}
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {showForm && form && (
                <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setShowForm(false)}>
                    <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-[#180A21] border border-[#2B1438] w-full max-w-3xl my-8 p-6 space-y-5 text-white" data-testid="quote-from-booking-modal">
                        <div className="flex justify-between items-center">
                            <div>
                                <h3 className="font-serif text-2xl text-white">Quotation from Booking</h3>
                                <div className="text-xs text-[#D4AF37] mt-1">{form.booking_number} · {form.booking_service_name}{form.booking_technician_name ? ` · Tech: ${form.booking_technician_name}` : ""}</div>
                            </div>
                            <button type="button" onClick={() => setShowForm(false)} className="text-white/40 hover:text-white text-2xl leading-none">×</button>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <Field label="Customer Name *">
                                <input required data-testid="qfb-cust-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="lx-in-qfb" />
                            </Field>
                            <Field label="Customer Email">
                                <input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="lx-in-qfb" />
                            </Field>
                            <Field label="Mobile">
                                <input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="lx-in-qfb" />
                            </Field>
                            <Field label="Company">
                                <input value={form.customer_company} onChange={(e) => setForm({ ...form, customer_company: e.target.value })} className="lx-in-qfb" />
                            </Field>
                        </div>
                        <Field label="Address">
                            <textarea rows={2} value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} className="lx-in-qfb resize-none" />
                        </Field>

                        <div className="grid grid-cols-3 gap-4">
                            <Field label="Issue Date">
                                <input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} className="lx-in-qfb" />
                            </Field>
                            <Field label="Valid Until">
                                <input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} className="lx-in-qfb" />
                            </Field>
                            <Field label="VAT %">
                                <input type="number" min="0" max="100" step="0.1" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} className="lx-in-qfb" />
                            </Field>
                        </div>

                        <LineItemsEditor
                            items={form.items}
                            onChange={(items) => setForm({ ...form, items })}
                            currency={form.currency}
                            vatRate={+form.vat_rate || 0}
                        />

                        <Field label="Notes (optional)">
                            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="lx-in-qfb resize-none" />
                        </Field>

                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-xs uppercase tracking-wider border border-[#2B1438] text-white/70">Cancel</button>
                            <button type="submit" data-testid="qfb-save-btn" disabled={saving} className="lx-btn-gold px-6 py-2 text-sm disabled:opacity-60">
                                {saving ? <Spinner /> : "Create Quotation"}
                            </button>
                        </div>
                        <style>{`.lx-in-qfb{width:100%;padding:0.6rem 0.75rem;background:#0F0614;border:1px solid #2B1438;color:white;font-size:.875rem;outline:none}.lx-in-qfb:focus{border-color:#D4AF37}`}</style>
                    </form>
                </div>
            )}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <div className="lx-label text-white/50 mb-1.5">{label}</div>
            {children}
        </label>
    );
}
